import type { FieldValues, SubmitHandler } from 'react-hook-form';
import { forwardRef, useState } from 'react';
import { Button, Stack } from '@sixui/ui';
import stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import type { IUsePersistentFormResult } from '~/components/utils/PersistentForm';
import { FormSection } from '~/components/atoms/FormSection';
import { ActionBar } from '~/components/molecules/ActionBar';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { PersistentFormBanner } from '~/components/utils/PersistentForm';

export type IContentBlockFormBodyProps<TFieldValues extends FieldValues> =
  IContainerProps &
    Omit<
      React.FormHTMLAttributes<HTMLFormElement>,
      'onChange' | 'onSubmit' | 'onReset' | 'onSubmitCapture'
    > & {
      form: IUsePersistentFormResult<TFieldValues>;
      children: React.ReactNode;
      onSubmit?: SubmitHandler<TFieldValues>;
      onCancel?: SubmitHandler<TFieldValues>;
      onFailure?: (error: unknown) => void;
      banners?: Array<React.ReactElement>;
      error?: unknown;
      submitAction?: React.ReactNode;
      submitButtonLabel?: string;
      cancelButtonLabel?: string;
      cancelAction?: React.ReactNode;
      cancelUrl?: string;
      actionBarAlwaysVisible?: boolean;
      hideActionBar?: boolean;
      first?: boolean;
      extended?: boolean;
      extraActions?: React.ReactNode;
    };

type IContentBlockFormBody = <TFieldValues extends FieldValues>(
  props: IContentBlockFormBodyProps<TFieldValues> & {
    ref?: React.Ref<HTMLFormElement>;
  },
) => React.ReactNode;

export const ContentBlockFormBody: IContentBlockFormBody = forwardRef(
  function ContentBlockFormBody(props, ref?: React.Ref<HTMLFormElement>) {
    const { t } = useTranslation();
    const {
      sx,
      form,
      children,
      onSubmit,
      onCancel,
      onFailure,
      banners,
      error: errorProp,
      submitAction,
      submitButtonLabel = t('common.save'),
      cancelButtonLabel = t('common.cancel'),
      cancelAction,
      cancelUrl,
      actionBarAlwaysVisible,
      hideActionBar,
      first,
      extended,
      extraActions,
      ...other
    } = props;

    const [submitError, setSubmitError] = useState<unknown>();
    const submitting = form.formState.isSubmitting ?? false;
    const showActionBar =
      !hideActionBar && (submitAction || onSubmit || cancelAction || cancelUrl);
    const error = errorProp ?? submitError;

    return (
      <form
        {...stylex.props(sx)}
        ref={ref}
        onSubmit={(event) => {
          // Prevent event propagation to other forms.
          event.stopPropagation();

          if (onSubmit) {
            void form
              .handleSubmit(onSubmit)(event)
              .catch((error) => {
                setSubmitError(error);
                onFailure?.(error);
              });
          }
          form.discard?.();
        }}
        {...other}
      >
        <ContentBlock.Body first={first} extended={extended}>
          <FormSection>
            {form.canRecover || banners?.length ? (
              <Stack gap={2}>
                {form.canRecover ? (
                  <PersistentFormBanner persistentForm={form} />
                ) : null}
                {banners}
              </Stack>
            ) : null}

            {children}
            {error ? <ErrorMessage error={error} /> : null}
          </FormSection>
        </ContentBlock.Body>

        {showActionBar ? (
          <ActionBar alwaysVisible={actionBarAlwaysVisible}>
            <ContentBlock.Footer>
              {submitAction ??
                (onSubmit ? (
                  <Button
                    type="submit"
                    loading={submitting}
                    disabled={submitting}
                  >
                    {submitButtonLabel}
                  </Button>
                ) : null)}

              {cancelAction ??
                (cancelUrl || onCancel ? (
                  <Button
                    href={cancelUrl}
                    variant="text"
                    onClick={() => {
                      form.discard?.();

                      return onCancel?.(form.getValues());
                    }}
                    disabled={submitting}
                  >
                    {cancelButtonLabel}
                  </Button>
                ) : null)}

              {extraActions}
            </ContentBlock.Footer>
          </ActionBar>
        ) : null}
      </form>
    );
  },
);
